import { Button, Card, Icon } from '@salesforce/design-system-react';
import { useState } from 'react';

import { IconCategory } from '../../../shared/types/Enum';
import { FieldHelpTooltip } from './FieldHelpTooltip';

interface SpecSectionPanelProps {
  /** Header-right slot — e.g. the "+ Add Component"/"+ Add Range" buttons. */
  action?: React.ReactNode;
  /** Restores this nested panel's own visible border (the real SLDS `.slds-card_boundary`
   * utility — see its own doc comment in the installed `card.css`: "Used to bring back the border
   * on a card when needed"). Defaults to `true`: every panel this is used for is itself a
   * `Card` nested inside the spec's own outer `Card`, and SLDS deliberately strips a nested
   * card's border/shadow by default (`.slds-card .slds-card { border: 0; box-shadow: none }`,
   * avoiding boxes-in-boxes) unless this modifier opts back in. Pass `false` for a panel the
   * Designer references show as an unboxed, merely labeled section instead (Configuration;
   * Adjustment Behavior's own header, whose boxing instead comes from its child Discount/Premium
   * rate cards — see ThresholdRowFields). */
  boundary?: boolean;
  children?: React.ReactNode;
  className?: string;
  /** Shown next to the title only while `collapsible` and the panel itself is closed — e.g. Price
   * Components' own selected-count badge, so the summary stays visible once its content is
   * hidden. Ignored while the panel is open. */
  collapsedBadge?: React.ReactNode;
  /** Adds a chevron toggle (defaults open) so the user can collapse this panel's content — for
   * panels tall/wide enough that several stacked at once gets crowded (e.g. Price Components and
   * Price Adjustment Ranges both full-width in the same Range card). */
  collapsible?: boolean;
  description?: string;
  /** Stretches the panel to fill its flex container's height (e.g. to match a sibling column) —
   * grows past that height instead of clipping if its content needs more room. */
  fillHeight?: boolean;
  /** SLDS utility icon name (e.g. "settings", "signature") — see the utility icon sprite at
   * public/assets/icons/utility-sprite. */
  iconName: string;
  /** Controlled open state — only needed by a caller that must force this open itself (e.g.
   * PriceComponentsTable reopening when a component is added). Falls back to internal state
   * (defaulting open) when omitted. */
  onToggle?: () => void;
  opened?: boolean;
  title: string;
  /** Extra detail shown via a help icon next to the title, for copy too long for `description`. */
  tooltip?: string;
}

/**
 * Extracted so computing the three-way className combination doesn't add to
 * SpecSectionPanel's own complexity.
 */
const getSectionCardClassName = (boundary: boolean, fillHeight?: boolean): string =>
  ['qfw-section-card', boundary && 'slds-card_boundary', fillHeight && 'qfw-section-card_fill']
    .filter(Boolean)
    .join(' ');

/**
 * Shared bordered-card header (icon + title + optional description/tooltip, optional header-right
 * action, optional collapse chevron) used by every QualitySpecCard section — Configuration,
 * Contracted Value, Applies to, Adjustment Behavior, Price Components, Price Adjustment Ranges —
 * so they read as one consistent family of panels instead of each section inventing its own
 * header markup. Built on the SLDS `Card` component, which already composes an icon + heading
 * into the standard Lightning card header for us.
 */
export const SpecSectionPanel: React.FC<SpecSectionPanelProps> = ({
  action,
  boundary = true,
  children,
  className: additionalClassName,
  collapsedBadge,
  collapsible,
  description,
  fillHeight,
  iconName,
  onToggle,
  opened: openedProp,
  title,
  tooltip,
}) => {
  const [openedState, setOpenedState] = useState(true);
  const opened = openedProp ?? openedState;
  const toggle =
    onToggle ??
    (() => {
      setOpenedState(previous => !previous);
    });
  const showContent = !collapsible || opened;

  const heading = (
    <span className="slds-grid slds-grid_vertical" style={{ alignItems: 'flex-start', minWidth: 0, width: '100%' }}>
      {/* `slds-gutters_xx-small` sets a *negative* inline margin on the element it's applied to
          (meant to be offset by `.slds-col` children's own compensating padding) — these plain
          `<span>` children never had that compensating padding, so the whole row (title included)
          shifted left into the header icon beside it. Plain inline `gap` avoids that entirely. */}
      <span className="slds-grid slds-grid_vertical-align-center" style={{ gap: 'var(--slds-g-spacing-1, 0.25rem)' }}>
        <span className="slds-truncate" title={title}>
          {title}
        </span>
        {tooltip ? <FieldHelpTooltip label={tooltip} /> : null}
        {collapsible && !opened && collapsedBadge ? (
          <span className="slds-badge slds-theme_shade">{collapsedBadge}</span>
        ) : null}
      </span>
      {description ? (
        <span
          className="slds-text-body_small slds-text-color_weak"
          style={{
            display: 'block',
            fontWeight: 'var(--slds-g-font-weight-4)',
            maxWidth: '100%',
            whiteSpace: 'normal',
          }}
        >
          {description}
        </span>
      ) : null}
    </span>
  );

  const className = [getSectionCardClassName(boundary, fillHeight), additionalClassName].filter(Boolean).join(' ');

  return (
    <Card
      bodyClassName={fillHeight ? 'qfw-card-body_fill' : undefined}
      className={className}
      headerActions={
        <div
          className="qfw-section-actions slds-grid slds-grid_vertical-align-center"
          style={{ gap: 'var(--slds-g-spacing-2, 0.5rem)' }}
        >
          {action}
          {collapsible ? (
            <Button
              assistiveText={{ icon: opened ? `Collapse ${title}` : `Expand ${title}` }}
              className="qfw-section-toggle"
              iconCategory={IconCategory.Utility}
              iconName={opened ? 'chevronup' : 'chevrondown'}
              onClick={toggle}
              variant="icon"
            />
          ) : null}
        </div>
      }
      heading={heading}
      icon={
        <Icon
          category={IconCategory.Utility}
          containerClassName={`slds-icon_container slds-icon-utility-${iconName}`}
          name={iconName}
          size="small"
          // Every section header icon in the Ash/Sulfur/BTU references (Configuration, Contracted
          // Value, Applies To, Adjustment Behavior, Price Components, Price Adjustment Ranges)
          // renders in brand blue, not the neutral gray `slds-current-color` otherwise inherits —
          // SLDS has no `colorVariant`/utility class for that (`'base' | 'default' | 'error' |
          // 'light' | 'warning' | 'success'` only), so this sets the real brand hook directly,
          // the same pattern already used for Discount/Premium's own directional icons.
          style={{ fill: 'var(--slds-g-color-accent-1)' }}
        />
      }
    >
      {showContent ? children : null}
    </Card>
  );
};
